import { Checkmark } from '@wfp/icons-react';
import { tooltipStyle } from '@wfp/react';
import { DynamicIcon } from 'acr_ui/sources/components/DynamicIcon';
import { Tooltip } from 'acr_ui/sources/components/Tooltip';
import { format, isValid } from 'date-fns';
import React from 'react';
import { useDispatch } from 'react-redux';

import { CURRENT_STATE_LABELS, NEW_DATE_FORMAT } from 'src/constants';
import { changeDatePicker } from 'src/redux/reports/actions';
import { calcMergeStatus } from 'src/utils';

export type DashboardType = 'project' | 'review' | 'publication';

export const dashboardTypes: Record<DashboardType, string> = {
  project: 'Project Status',
  review: 'Review Status',
  publication: 'Publication Status',
};

export interface Header {
  key: string;
  title: string;
}

export const headers: Header[] = [
  { key: 'project_code', title: 'NUMBER' },
  { key: 'category', title: 'CATEGORY' },
  { key: 'project_regional_bureau_display_code', title: 'REGIONAL OFFICE' },
  { key: 'project_country', title: 'COUNTRY' },
  { key: 'project_type', title: 'PROJECT TYPE' },
  { key: 'current_status', title: 'CURRENT STATUS' },
  { key: 'initial_draft', title: 'INITIAL DRAFT' },
  { key: 'review', title: 'REVIEW' },
  { key: 'cd_approval', title: 'CD APPROVED' },
  { key: 'rd_approval', title: 'RD APPROVAL' },
  { key: 'rd_approved', title: 'RD APPROVED' },
  { key: 'published', title: 'PUBLISHED' },
];

interface FilterTextProps {
  column: {
    setFilter: (value: string) => void;
  };
}

export function filterText({
  column: { setFilter },
}: FilterTextProps): JSX.Element {
  return (
    <input
      type="text"
      onChange={event => setFilter(event.currentTarget.value)}
      style={{ width: '100%' }}
      data-test-id="operation-id-search"
    />
  );
}

interface FilterSelectProps {
  column: {
    filterValue?: string[] | null;
    setFilter: (value: string[] | null) => void;
  };
}

type GetOptionsFunction = (id: string) => JSX.Element[];

export function createFilterSelect(getOptionsFunction: GetOptionsFunction) {
  return function filterSelect(
    id: string,
    { column: { filterValue, setFilter } }: FilterSelectProps,
    isEqual?: boolean,
  ): JSX.Element {
    const options = getOptionsFunction(id);

    function handleSetSelectFilter(
      event: React.ChangeEvent<HTMLSelectElement>,
      setFilter: (value: string[] | null) => void,
      isEqual = false,
    ) {
      let selectedOptions: string[] | null = [];

      if (event.target.value === 'all') {
        if (isEqual) {
          selectedOptions = null;
        }
      } else {
        selectedOptions = [event.target.value];
      }

      setFilter(selectedOptions);
    }

    return (
      <select
        style={{ width: '100%' }}
        value={filterValue ? filterValue[0] : ''}
        onChange={event => {
          handleSetSelectFilter(event, setFilter, isEqual);
        }}
      >
        {options}
      </select>
    );
  };
}

export interface ReportData {
  [key: string]: any;
  pk?: number;
  project_code?: string;
  current_status?: string;
  merge_status?: string;
}

export function compareAsStrings(a: unknown, b: unknown): number {
  const sa = String(a);
  const sb = String(b);
  if (sa < sb) return -1;
  if (sa > sb) return 1;
  return 0;
}

export function createGetOptions(
  data: ReportData[],
  listStatus: DashboardType,
) {
  return function getOptions(key: string): JSX.Element[] {
    let processedData = data;

    if (listStatus === 'publication') {
      processedData = data?.map(reportRow => {
        const mergeStatus = calcMergeStatus(reportRow);
        const { current_status } = reportRow;

        return {
          ...reportRow,
          current_status,
          merge_status: mergeStatus,
        };
      });
    }

    let value_list =
      processedData
        ?.filter(reportData => reportData[key])
        ?.map(reportData => reportData[key])
        ?.sort(compareAsStrings) || [];

    value_list = [...new Set(value_list)];

    if (!value_list.length) return [];

    const options = value_list.map((value, index) => {
      const displayValue =
        listStatus === 'publication' && key === 'current_status'
          ? CURRENT_STATE_LABELS[value] || value
          : value;

      return (
        <option key={index + 1} value={value}>
          {displayValue}
        </option>
      );
    });

    const defaultOption = (
      <option key={0} value="all">
        -/-
      </option>
    );

    return [defaultOption, ...options];
  };
}

interface DateHandlers {
  handleDateChange: (date: Date, state: string, reportPk: number) => void;
  handleDateReset: (state: string, reportPk: number) => void;
}

export function useDateHandlers(currentPeriod?: string): DateHandlers {
  const dispatch = useDispatch();

  const handleDateChange = (date: Date, state: string, reportPk: number) => {
    if (date && isValid(date)) {
      dispatch(
        changeDatePicker(
          format(date, NEW_DATE_FORMAT),
          state,
          reportPk,
          currentPeriod,
        ),
      );
    }
  };

  const handleDateReset = (state: string, reportPk: number) => {
    dispatch(changeDatePicker(null, state, reportPk, currentPeriod));
  };

  return { handleDateChange, handleDateReset };
}

interface ReviewIcon {
  icon: string;
  icon_url: string;
  title: string;
  checked: boolean;
}

export function generateReviewIcons(
  icons: ReviewIcon[],
  checked_stack_count: number,
  total_stack_count: number,
): JSX.Element {
  const iconSet = icons.map((icon, index) => {
    return (
      <figure className="rb-review-icon" key={`${icon.icon}-${index}`}>
        <DynamicIcon
          url={icon.icon_url}
          width={18}
          height={18}
          disableAutoFillPaths={['icon-sdg', 'icon-environment'].includes(
            icon.icon,
          )}
          customClass={`review-icon-aws ${
            icon.icon === 'icon-sdg' ? 'icon-sdg ' : ''
          } ${icon.checked ? 'review-status' : ''}`}
        />
        <figcaption>{icon.title}</figcaption>
        {icon.checked && (
          <Checkmark
            color="#000"
            className="review-checked"
            description="Checked"
          />
        )}
      </figure>
    );
  });

  const reviewCount = `${checked_stack_count} out of ${total_stack_count}`;

  return (
    <span className="review-icon-tooltip-cell">
      {total_stack_count > 0 ? (
        <Tooltip
          position="left"
          title={<div className="review-icon-tooltip">{iconSet}</div>}
          tooltipStyle={tooltipStyle}
          theme="light review-icon-tooltip-wrapper"
          trigger="click"
        >
          {reviewCount}
        </Tooltip>
      ) : (
        reviewCount
      )}
    </span>
  );
}
